import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { twMerge } from 'tailwind-merge';

import ErrorBoundary from 'components/ErrorBoundary';

import { landingQueryKeys, topbarQueryKeys } from 'lib/queryKeys';

import { getNavbarAds } from 'services/ads';
import { getLandingsList } from 'services/landing';

import FeaturedLandingLink from '../FeaturedLandings/FeaturedLandingLink';
import NavItem from '../NavItem';

const CombinedNavContent = ({
  addContentRef,
  className,
  gaEventCategory,
  overflowMap = {},
  showOnlyOverflowed = false,
  startIndex = 0,
}) => {
  const { data: navbarAds = [], isLoading: isLoadingAds } = useQuery({
    queryKey: topbarQueryKeys.navbarAds(),
    queryFn: ({ signal }) => getNavbarAds({ signal }),
    select: (data) => data?.sort((a, b) => a.position - b.position),
    enabled: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const { data: featuredLandings = [], isLoading: isLoadingLandings } =
    useQuery({
      queryKey: landingQueryKeys.featured(),
      queryFn: ({ signal }) => getLandingsList({ featured: true }, { signal }),
      select: (data) => data.landings,
      enabled: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    });

  const isLoading = isLoadingAds || isLoadingLandings;
  const hasContent = navbarAds?.length > 0 || featuredLandings?.length > 0;

  if (isLoading || !hasContent) {
    return null;
  }

  const shouldShowItem = (index) => {
    if (showOnlyOverflowed) {
      return overflowMap[index] === true;
    }

    return overflowMap[index] !== true;
  };

  return (
    <>
      {navbarAds.map(
        (
          {
            id,
            event,
            title,
            image,
            description,
            sameTab,
            notificationIcon,
            url,
          },
          index
        ) => {
          const itemIndex = startIndex + index;

          if (!shouldShowItem(itemIndex) || !id || !url) {
            return null;
          }

          return (
            <ErrorBoundary key={`ad-${id}-${itemIndex}`}>
              <div
                ref={(el) => addContentRef && addContentRef(itemIndex, el)}
                className={twMerge('flex gap-2', className)}
              >
                <NavItem
                  gaEventAction={event || `${title}_tab`}
                  gaEventCategory={gaEventCategory}
                  imageProps={{
                    src: image,
                    alt: description,
                  }}
                  sameTab={sameTab}
                  text={title}
                  unreadStatus={notificationIcon}
                  url={url}
                />
              </div>
            </ErrorBoundary>
          );
        }
      )}
      {featuredLandings.map(
        ({ landingId, landingName, landingSlug, landingType }, index) => {
          const itemIndex = startIndex + navbarAds.length + index;

          if (!shouldShowItem(itemIndex) || !landingId || !landingSlug) {
            return null;
          }

          return (
            <ErrorBoundary key={`landing-${landingId}-${itemIndex}`}>
              <div
                ref={(el) => addContentRef && addContentRef(itemIndex, el)}
                className={twMerge('flex gap-2', className)}
              >
                <FeaturedLandingLink
                  gaEventCategory={gaEventCategory}
                  landingId={landingId}
                  landingName={landingName}
                  landingSlug={landingSlug}
                  landingType={landingType}
                />
              </div>
            </ErrorBoundary>
          );
        }
      )}
    </>
  );
};

CombinedNavContent.propTypes = {
  addContentRef: PropTypes.func,
  className: PropTypes.string,
  gaEventCategory: PropTypes.string.isRequired,
  overflowMap: PropTypes.object,
  showOnlyOverflowed: PropTypes.bool,
  startIndex: PropTypes.number,
};

CombinedNavContent.displayName = 'CombinedNavContent';

export default CombinedNavContent;
